<template>
  <div>
    <v-toolbar flat class="rounded-b-0 mr-8" style="background: transparent;">
      <v-spacer />

      <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
        <template #activator="{ on }">
          <v-btn icon v-on="on" @click="toggleGrouping">
            <v-badge color="appBar" left :value="groupingState" overlap :content="$tc(groupingName)">
              <v-icon>calendar_view_day</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $tc(groupingTooltip) }}</span>
      </v-tooltip>

      <template v-if="!isPopup">
        <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
          <template #activator="{ on }">
            <v-btn icon v-on="on" @click="openPopup">
              <v-icon outline>
                aspect_ratio
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('popup_window') }}</span>
        </v-tooltip>
      </template>

      <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
        <template #activator="{ on }">
          <v-btn icon v-on="on" :disabled="items.length <= 0" @click="reloadSessions">
            <v-icon outline>
              autorenew
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $tc('reload') }}</span>
      </v-tooltip>

      <template v-if="isStarted">
        <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
          <template #activator="{ on }">
            <v-btn icon v-on="on" @click="stopSessions">
              <v-icon outline>
                pause
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('stop') }}</span>
        </v-tooltip>
      </template>

      <template v-else>
        <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
          <template #activator="{ on }">
            <v-btn id="play_session_monitor" icon v-on="on" @click="startSessions">
              <v-icon outline>
                play_arrow
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('start') }}</span>
        </v-tooltip>
      </template>
    </v-toolbar>

    <v-card
      id="session_monitor"
      :class="{
        'rounded-lg': !isPopup,
        'rounded-0': isPopup,
        'h-100v': isPopup,
        'h-85v': !isPopup && $vuetify.breakpoint.xsOnly,
        'h-70v': !isPopup && $vuetify.breakpoint.smAndUp
      }"
      class="mx-md-10 ml-2 mb-4"
      flat
      outlined
    >
      <template v-if="Object.keys(items).length <= 0 && isStarted">
        <v-col class="pr-3 pl-3">
          <v-progress-linear :indeterminate="true" />
        </v-col>
      </template>

      <template v-if="sessionCounter <= 0 && !isStarted">
        <v-container fluid fill-height>
          <v-row align="center" justify="center">
            <v-col cols="12" sm="8" md="4">
              <v-row justify="center">
                <v-btn @click="startSessions" icon width="120" height="120">
                  <v-icon color="timid" size="120">
                    play_circle_outline
                  </v-icon>
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </template>

      <v-subheader fixed class="pl-4">
        {{ sessionCounter }} {{ $tc('active_session', 2) }}
      </v-subheader>

      <v-divider />

      <v-virtual-scroll
        :items="Object.entries(items)"
        :item-height="104"
        bench="1"
      >
        <template #default="{ index, item }">
          <template v-if="item.length === 1 || groupingState === 0">
            <!-- item -->

            <v-list-item :key="`sessions-${index}`">
              <v-list-item-avatar>
                <v-icon>account_box</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-subtitle>{{ item[1][0].date }}</v-list-item-subtitle>
                <v-list-item-title>{{ item[1][0].name }}</v-list-item-title>
                <v-row no-gutters>
                  <v-col cols="auto" class="pr-2">
                    <v-list-item-subtitle>{{ item[1][0].id }}</v-list-item-subtitle>
                  </v-col>
                  <v-col cols="auto" class="pr-2">
                    <v-list-item-subtitle>{{ item[1][0].ip }}</v-list-item-subtitle>
                  </v-col>
                  <v-col cols="auto">
                    <v-list-item-subtitle>{{ item[1][0].tls }}</v-list-item-subtitle>
                  </v-col>
                </v-row>
              </v-list-item-content>
            </v-list-item>
            <!-- item -->
          </template>
          <!-- Group -->
          <template v-else>
            <v-expansion-panels flat :key="index">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <v-badge left color="appBar" :value="item[1].length" :content="item[1].length">
                    {{ item[1][0][groupingName] }}
                  </v-badge>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-for="value in item[1]" :key="value.id">
                  <!-- item -->
                  <v-list-item :key="`sessions-${index}`">
                    <v-list-item-avatar>
                      <v-icon>account_box</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-subtitle>{{ value.date }}</v-list-item-subtitle>
                      <v-list-item-title>{{ value.name }}</v-list-item-title>
                      <v-row no-gutters>
                        <v-col cols="auto" class="pr-2">
                          <v-list-item-subtitle>{{ value.id }}</v-list-item-subtitle>
                        </v-col>
                        <v-col cols="auto" class="pr-2">
                          <v-list-item-subtitle>{{ value.ip }}</v-list-item-subtitle>
                        </v-col>
                        <v-col cols="auto">
                          <v-list-item-subtitle>{{ value.tls }}</v-list-item-subtitle>
                        </v-col>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- item -->
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
          <v-divider :key="`sessions-divider-${index}`" />
        </template>
      </v-virtual-scroll>
    </v-card>
  </div>
</template>

<script>
export default {
  props: {
    startWithBlock: {
      type: Boolean,
      required: true
    }
  },
  created () {
    if (this.startWithBlock === true) {
      this.startSessions()
    }
  },
  beforeDestroy () {
    this.stopSessions()
  },
  data () {
    return {
      popup: null
    }
  },
  computed: {
    sessionCounter () {
      return this.$store.state.sessionMon.sessionCounter
    },
    groupingState () {
      return this.$store.state.sessionMon.grouping
    },
    groupingName () {
      switch (this.groupingState) {
        case 1:
          return 'name'
        case 2:
          return 'ip'
        default:
          return ''
      }
    },
    groupingTooltip () {
      switch (this.groupingState) {
        case 1:
          return 'grouping_by_name'
        case 2:
          return 'grouping_by_ip'
        default:
          return 'grouping_by_none'
      }
    },
    items () {
      return this.$store.getters['sessionMon/GET_SESSIONS']
    },
    isStarted () {
      return this.$store.state.sessionMon.sessionsReq !== null
    },
    isPopup () {
      return this.$route.name === 'monitor-sessions-popup'
    }
  },
  methods: {
    toggleGrouping () {
      this.$store.commit('sessionMon/TOGGLE_SESSION_GROUPING')
    },
    startSessions () {
      this.$store.dispatch('sessionMon/start')
    },
    stopSessions () {
      this.$store.dispatch('sessionMon/stop')
    },
    reloadSessions () {
      this.$store.commit('sessionMon/SET_SESSIONS')
    },
    openPopup () {
      this.stopSessions()
      const name = 'monitor-sessions-popup'
      const route = this.$router.resolve({ name })

      this.$store
        .dispatch('ui/setPopup', {
          exists: this.popup,
          url: route.href
        })
        .then(res => {
          const self = this
          if (res) {
            res.addEventListener('beforeunload', () => {
              self.popup.close()
              self.popup = null
            })

            self.popup = res
          }
        })
    }
  },
  watch: {
    startWithBlock (val) {
      if (val === true) {
        this.startSessions()
      }
    }
  }
}
</script>
